import { useState } from 'react';
import { ChatMetadata, Message, Sender } from '../types';
import { apiUrl, getRecaptchaToken } from '../utils';
import { useTranslation } from 'react-i18next';
import { ChatMetadataParams, useChatMetadata } from '@/hooks/useChatMetadata';
import { Content } from '@/sharedType';

export default function useConversationMessage({ chatId, sessionID }: ChatMetadataParams) {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const { data: chatMetadata } = useChatMetadata({ chatId, sessionID });
  const { t } = useTranslation();

  const ERROR_MSG = t('errors.generalError');

  function appendMessages(newMessages: Message | Message[]) {
    setMessages((prevMessages) => [
      ...prevMessages,
      ...(Array.isArray(newMessages) ? newMessages : [newMessages]),
    ]);
  }

  // Used in Web
  async function sendMessage(text: string, fileList: File[]) {
    if (loading || !chatMetadata) {
      return;
    }
    setLoading(true);

    const content: Content = [{ type: 'text' as const, text }];

    fileList?.forEach((file) => {
      if (file.type.includes('image')) {
        content.push({ type: 'image' as const, image: URL.createObjectURL(file) });
      }
      if (file.type === 'application/pdf') {
        content.push({
          type: 'file' as const,
          data: URL.createObjectURL(file),
          mediaType: file.type,
        });
      }
    }),
      appendMessages({ sender: Sender.User, content });

    // NOTE: this used to be 'messages.length === 1' but i think we must take into account new conversations without first msg
    if (messages.length <= 1) {
      await startConversation();
    }
    const result = await postMessage({
      message: text,
      fileList,
      sessionID,
    });

    appendMessages({ ...result, sender: Sender.Assistant });
    setLoading(false);
  }

  // Used in Playground
  async function sendEditorMessage(text: string, fileList: File[] = []) {
    setLoading(true);

    const content: Content = [
      { type: 'text' as const, text },
      ...(fileList?.map((file) => ({ type: 'image' as const, image: URL.createObjectURL(file) })) ??
        []),
    ];

    appendMessages({ sender: Sender.User, content });
    const formData = new FormData();
    formData.append('message', text);
    formData.append('chatId', chatId);
    formData.append(
      'conversation',
      JSON.stringify(messages.concat({ sender: Sender.User, content: [{ type: 'text', text }] })),
    );
    fileList.forEach((file) => {
      formData.append('files', file);
    });

    const result = await fetch(`${apiUrl}/web/conversation/${chatId}`, {
      method: 'POST',
      body: formData,
    });

    if (result.ok) {
      const json = await result.json();
      appendMessages({ sender: Sender.Assistant, content: json.content });
    } else {
      console.error('Server error');
      appendMessages({ sender: Sender.Assistant, content: [{ type: 'text', text: ERROR_MSG }] });
    }

    setLoading(false);
  }

  function deleteMessage(index: number) {
    setMessages((prev) => prev.filter((_, i) => i !== index));
  }

  async function startConversation() {
    await fetch(`${apiUrl}/web/start/${chatId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + (await getRecaptchaToken()),
      },
      body: JSON.stringify({ sessionID }),
    });
  }
  async function postMessage(payload: {
    message: string;
    sessionID: string;
    fileList: File[];
  }): Promise<Message> {
    try {
      const formData = new FormData();

      formData.append('message', payload.message);
      formData.append('sessionID', payload.sessionID);

      payload.fileList.forEach((file) => {
        formData.append('files', file);
      });

      const result = await fetch(`${apiUrl}/web/message/${chatMetadata?.id}`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + (await getRecaptchaToken()),
        },
        body: formData,
      });

      if (!result || !result.ok) {
        throw new Error('No Response');
      }

      return result.json();
    } catch (e) {
      return {
        sender: Sender.Assistant,
        content: [{ type: 'text', text: ERROR_MSG }],
      };
    }
  }

  async function initChat(chatMetadata: ChatMetadata) {
    const fetchedMessagesLen = chatMetadata.conversations[0]?.messages.length;
    if (fetchedMessagesLen) {
      if (fetchedMessagesLen > messages.length) {
        for (const message of chatMetadata.conversations[0].messages) {
          appendMessages({
            sender: message.sender,
            content: message.content,
          });
        }
      }
    } else if (chatMetadata.firstMessage) {
      setMessages([
        {
          sender: Sender.Assistant,
          content: [{ type: 'text', text: chatMetadata.firstMessage }],
        },
      ]);
    } else {
      setMessages([]);
    }
  }

  return {
    initChat,
    chatMetadata,
    loading,
    sendEditorMessage,
    messages,
    sendMessage,
    setLoading,
    deleteMessage,
    startConversation,
    setMessages,
  };
}
